import {useEffect, useState} from "react";
import add_above from "../../assets/add_row_above.svg"
import add_below from "../../assets/add_row_below.svg"
import delete_row from "../../assets/delete.svg"
import plus from "../../assets/plus.svg";
import valuer_api from "../../api/valuer";

const ValueAmendPasteableTableContainer = (props) => {

    // For Saving
    const summaryId = props.summaryId
    const section = props.sectionName
    const identifier = props.identifier

    // Required
    const columns = props.columns

    // Optional
    const addRows = props.addRows ? props.addRows : false
    const deleteRows = props.deleteRows ? props.deleteRows : false
    const sideBarMenu = props.sideBarMenu ? props.sideBarMenu : false
    const addButtonText = props.addButtonText ? props.addButtonText : false

    const blankRows = props.blankRows ? props.blankRows : 0

    // Value
    const [values, setValues] = useState({})

    // Sizing
    const rowHeight = props.rowHeight ? props.rowHeight : '48px'

    // Saving Tracker
    const UpdateParentValue = props.UpdateParentValue
    const AddToQueue = props.AddToQueue
    const RemoveFromQueue = props.RemoveFromQueue
    const AddToErroredSaves = props.AddToErroredSaves

    // Error Handling
    const setErrorMessage = props.setErrorMessage
    const setErrorTitle = props.setErrorTitle


    useEffect(() => {
        let base = props.values ?? {};

        // Incoming props data: trim trailing blank rows (no freshRow here)
        base = trimTrailingEmptyRows(base, columns);

        const minRows = Math.max(Number.isInteger(blankRows) ? blankRows : 0, 1);
        const seeded = ensureRows(base, minRows);
        setValues(seeded)
    }, [props.values, blankRows, columns]);

    const trimTrailingEmptyRows = (obj, columns) => {
        const entries = Object.entries(obj)
            .sort(([, a], [, b]) => (a?.order ?? 0) - (b?.order ?? 0));

        while (entries.length > 0) {
            const [key, row] = entries[entries.length - 1];

            if (row?.freshRow) break;

            const isEmpty = columns.every(c => {
                const key = c.identifier;
                return key ? (row?.[key] === "" || row?.[key] === null || row?.[key] === undefined) : true;
            });
            if (isEmpty) {
                entries.pop();
            } else {
                break;
            }
        }

        const cleaned = {};
        entries.forEach(([key, row], i) => {
            row.order = i + 1;
            cleaned[key] = row;
        });

        return cleaned;
    };


    // Ensure minimum number of rows (seed UI)
    const ensureRows = (obj, minRows) => {
        const keys = Object.keys(obj).sort((a,b) => ((obj[a]?.order ?? 0) - (obj[b]?.order ?? 0)));
        const arr = keys.map(k => ({ key: k, row: { ...obj[k] } }));

        while (arr.length < minRows) {
            arr.push({ key: `tmp-${Date.now()}-${arr.length}`, row: { ...getNewRow(), freshRow: true } });
        }

        arr.forEach((e, i) => { e.row.order = i + 1; });

        const next = {};
        arr.forEach(({ key, row }) => { next[key] = row; });
        return next;
    };

    const sanitizeRowsForPersistence = (rowsArray) => {
        return rowsArray.map(r => {
            const cleaned = { ...r };
            delete cleaned.freshRow;   // UI-only field removed
            return cleaned;
        });
    };

    const persistValues = (snapshot) => {
        const updateTime = Date.now();
        // Build ordered copy
        const ordered = Object.values(snapshot).sort((a,b) => a.order - b.order);
        // Strip UI flags
        const cleanedForDb = sanitizeRowsForPersistence(ordered);

        AddToQueue(section, identifier, updateTime);
        UpdateParentValue(identifier, snapshot);

        valuer_api.ValuerAmendPut(summaryId, section, identifier, { value: cleanedForDb })
            .then(() => RemoveFromQueue(section, identifier, updateTime))
            .catch(() => {
                AddToErroredSaves(section, identifier, updateTime);
                setErrorTitle("Failed to Save");
                setErrorMessage("The request to save a field within the table has failed");
            });
    };

    const pasteTable = (ev, startingX, startingY) => {
        const data = (ev.clipboardData || window.clipboardData).getData("text");
        if (!data) return;

        const tabCount = (data.match(/\t/g) || []).length;
        if (tabCount === 0 && (data.match(/\r?\n/g) || []).length > 0) {
            const normalized = data.replace(/\r?\n+/g, " ");
            ev.preventDefault();
            setValues(prev => {
                const snapshot = ensureRows(prev, startingY + 1);
                const orderedKeys = Object.keys(snapshot).sort((a,b) =>
                    (snapshot[a]?.order ?? 0) - (snapshot[b]?.order ?? 0)
                );
                const targetRow = snapshot[orderedKeys[startingY]];
                const col = columns[startingX];
                if (col?.identifier) {
                    targetRow[col.identifier] = handleChange(normalized, col.type);
                    persistValues(snapshot);
                }
                return { ...snapshot };
            });
            return;
        }

        const grid = data
            .split(/\r?\n/)
            .filter(r => r.length)
            .map(r => r.split(/\t/));

        if (!grid.length) return;

        setValues(prev => {
            let snapshot = ensureRows(prev, startingY + grid.length);

            const orderedKeys = Object.keys(snapshot).sort((a,b) =>
                (snapshot[a]?.order ?? 0) - (snapshot[b]?.order ?? 0)
            );
            const rowsArray = orderedKeys.map(k => ({ key: k, row: { ...snapshot[k] } }));

            for (let gy = 0; gy < grid.length; gy++) {
                const rIdx = startingY + gy;

                while (rowsArray.length <= rIdx) {
                    rowsArray.push({ key: `tmp-${Date.now()}-${rowsArray.length}`, row: { ...getNewRow(), freshRow: true } });
                }
                const targetRow = rowsArray[rIdx].row;

                for (let gx = 0; gx < grid[gy].length; gx++) {
                    const cIdx = startingX + gx;
                    if (cIdx >= columns.length) continue;
                    const col = columns[cIdx];
                    if (!col?.identifier) continue;

                    const raw = String(grid[gy][gx] ?? "");
                    targetRow[col.identifier] = handleChange(raw.replace(",", ""), col.type);
                    delete targetRow.freshRow;     // once typed, not a "fresh" trailing row
                }
            }

            rowsArray.forEach((e,i) => { e.row.order = i + 1; });
            const next = {};
            rowsArray.forEach(({ key, row }) => { next[key] = row; });

            persistValues(next);
            return next;
        });
    };


    const getNewRow = () => {
        let new_row = {}
        for (let x = 0; x < columns.length; x++) {
            if (columns[x].identifier && columns[x].default) new_row[columns[x].identifier] = columns[x].default
            else if (columns[x].identifier) new_row[columns[x].identifier] = ""
        }
        return new_row
    }

    const AddNewRow = () => {
        InsertRow(Object.keys(values).length)
    }

    const DeleteRow = (rowKey) => {
        const updateTime = Date.now();

        let newValues = { ...values };
        delete newValues[rowKey];

        const totalCountBefore = Object.keys(values).length;
        let ordered = Object.values(newValues)
            .sort((a, b) => a.order - b.order)
            .map((item, i) => ({ ...item, order: i + 1 }));

        // Maintain UI blank row at bottom (not DB)
        while (ordered.length < totalCountBefore) {
            const newBlankRow = { ...getNewRow(), freshRow: true };
            newBlankRow.order = ordered.length + 1;
            ordered.push(newBlankRow);
        }

        const finalValues = {};
        ordered.forEach((item, idx) => finalValues[idx] = item);

        UpdateParentValue(identifier, finalValues);
        AddToQueue(section, identifier, updateTime);

        // Strip before saving, don't want to risk sending freshRow to db
        const cleanedForDb = sanitizeRowsForPersistence(ordered);

        valuer_api.ValuerAmendPut(summaryId, section, identifier, { value: cleanedForDb })
            .then(() => RemoveFromQueue(section, identifier, updateTime))
            .catch(() => {
                AddToErroredSaves(section, identifier, updateTime);
                setErrorTitle("Failed to Save");
                setErrorMessage("The request to delete a row failed");
            });
    };


    const InsertRow = (idx) => {
        let updateTime = Date.now();

        let v_copy = [...Object.values(values)].sort((a, b) => a.order - b.order)
        let _new_vs = [...v_copy.slice(0, idx), { ...getNewRow(), freshRow: true }, ...v_copy.slice(idx)]

        for (let x = 1; x <= _new_vs.length; x++) {
            _new_vs[x-1]["order"] = x
        }
        const cleanedForDb = sanitizeRowsForPersistence(_new_vs)

        AddToQueue(section, identifier, updateTime)
        let response = valuer_api.ValuerAmendPut(summaryId, section, identifier, { value: cleanedForDb });
        response.then(response => {
                UpdateParentValue(identifier, _new_vs)
                RemoveFromQueue(section, identifier, updateTime)
            }
        ).catch((err) => {
            AddToErroredSaves(section, identifier, updateTime)
            setErrorTitle("Failed to Save")
            setErrorMessage("The request to save a field within the table has failed")
        })
    }

    const ClickOff = () => {
        if (!identifier || !section || !summaryId) return
        SaveAll()
    }

    const SaveAll = () => {
        let updateTime = Date.now()

        const ordered = [...Object.values(values)].sort((a, b) => a.order - b.order)
        ordered.forEach((r,i) => r.order = i + 1)

        const cleanedForDb = sanitizeRowsForPersistence(ordered)

        AddToQueue(section, identifier, updateTime)
        UpdateParentValue(identifier, values)

        let response = valuer_api.ValuerAmendPut(summaryId, section, identifier, { value: cleanedForDb });
        response.then(
            () => RemoveFromQueue(section, identifier, updateTime)
        ).catch((err) => {
            AddToErroredSaves(section, identifier, updateTime)
            setErrorTitle("Failed to Save")
            setErrorMessage("The request to save a field within the table has failed")
        })
    }

    const handleChange = (value, type) => {
        if (!value && !(value === 0 || value === "")) return "";
        let _value = value
        switch (type) {
            case "price":
                _value = _value.replace(new RegExp("[a-zA-Z]+", "g"), "").replace("£", "")
                if (_value) {
                    _value = "£" + _value.toLocaleString();
                } else {
                    _value = ""
                }
                break;
            case "integer":
                _value = _value.replace(new RegExp(",", "g"), "")
                if (parseInt(_value)) {
                    _value = parseInt(_value).toLocaleString();
                } else {
                    _value = ""
                }
                break;
            case "float":
                _value = _value.replace(new RegExp("[a-zA-Z]+", "g"), "")
                break;
        }
        return _value;
    }

    return (
        <div className="valuer-amend-table-field-container">
            <div className="valuer-amend-table">
                <div className="valuer-amend-table-row-container">
                    {columns && columns.map((column, idx) => {
                        return <div className={"valuer-amend-table-field-column-container title" + (idx === 0 ? ' left' : '')}
                                    style={{width: column.width}} dangerouslySetInnerHTML={{__html: column.label}}/>
                    })}
                </div>
                {values && Object.entries(values)
                    .sort(([,a],[,b]) => (a?.order - b?.order))
                    .map(([rowKey, value], y_idx) => {
                        return (
                            <>
                                <div className={"valuer-amend-table-row-container" + (y_idx === Object.keys(values).length - 1 ? ' bottom' : '')}>
                                    {columns && columns.map((column, x_idx) => {
                                        return (
                                            <div className={"valuer-amend-table-field-column-container" + (x_idx === 0 ? ' left' : '')}
                                                 style={{width: column.width}}>
                                                <div className="valuer-amend-table-free-text-container">
                                                    <input type="text" value={value?.[columns[x_idx].identifier] ?? ''} disabled={!columns[x_idx].editable}
                                                           onChange={(ev) => {
                                                               setValues(v => {
                                                                   const col = columns[x_idx];
                                                                   const columnId = col?.identifier;
                                                                   if (!columnId) return v;

                                                                   const next = { ...v };
                                                                   const row  = { ...(next[rowKey] ?? value) };
                                                                   row[columnId] = handleChange(ev.target.value.replace(",", ""), col.type);
                                                                   delete row.freshRow;  // Once user types, it's a real row

                                                                   for (const _col of columns) {
                                                                       if (_col.type === 'calculated-price') {
                                                                           let _values = []
                                                                           for (const calcValue of _col.calculationVals) {
                                                                               let _val = parseInt(String(row[calcValue]).replace('£', '').replaceAll(',', ''))
                                                                               _values.push(_val)
                                                                           }
                                                                           if (!_values.includes(NaN)) {
                                                                               row[_col.identifier] = `£${Math.round(_values[0] / _values[1])}`
                                                                           }
                                                                       }
                                                                   }

                                                                   next[rowKey] = row;
                                                                   return next;
                                                               });
                                                           }}

                                                           onPaste={(ev) => {
                                                               const text = ev.clipboardData.getData("text");
                                                               if (text.includes("\t") || text.includes("\n")) {
                                                                   ev.preventDefault();
                                                                   pasteTable(ev, x_idx, y_idx);
                                                               }
                                                           }}
                                                           onBlur={() => ClickOff()}
                                                    />
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                                {(addRows || deleteRows) && sideBarMenu ?
                                    <div className="valuer-amend-table-row-options-container">
                                        {addRows ?
                                            <div className="valuer-amend-table-row-button-container add link"
                                                 onClick={() => InsertRow(y_idx)}>
                                                <div className="valuer-amend-table-row-button-img-container">
                                                    <img src={add_above} alt="Add Above Row"/>
                                                </div>
                                                <div className="valuer-amend-table-row-button-text-container">
                                                    Add Row Above
                                                </div>
                                            </div> : null
                                        }
                                        {deleteRows ?
                                            <div className="valuer-amend-table-row-button-container delete link"
                                                 onClick={() => DeleteRow(rowKey)}>
                                                <div className="valuer-amend-table-row-button-img-container">
                                                    <img src={delete_row} alt="Delete Row"/>
                                                </div>
                                                <div className="valuer-amend-table-row-button-text-container">
                                                    Delete Row
                                                </div>
                                            </div> : null
                                        }
                                        {addRows ?
                                            <div className="valuer-amend-table-row-button-container add link"
                                                 onClick={() => InsertRow(y_idx + 1)}>
                                                <div className="valuer-amend-table-row-button-img-container">
                                                    <img src={add_below} alt="Add Below Row"/>
                                                </div>
                                                <div className="valuer-amend-table-row-button-text-container">
                                                    Add Row Below
                                                </div>
                                            </div> : null
                                        }
                                    </div> : null
                                }

                            </>
                        )
                    })}
                {addRows && addButtonText ?
                    <div className="valuer-amend-custom-multi-select-button-container link"
                         onClick={() => AddNewRow()}>
                        <img src={plus} alt={"Add"}/>
                        <div>{addButtonText ? addButtonText : "Add"}</div>
                    </div> : null
                }
            </div>
        </div>
    )
}

export default ValueAmendPasteableTableContainer